import { Action } from '@ngrx/store';
import { TechnicalProductOP, ValueAddedOP } from '@cemex-core/cmx-api';

import { createReducer, on } from '@ordersnproducts/utils/create-reducer';
import OrdersCatalogsActions from '../actions/orders-catalogs.action';

export interface ValueAddedTuple {
  loading: boolean;
  valuesAdded: ValueAddedOP[];
  technicalProducts: TechnicalProductOP[];
}

export interface ValuesAddedState {
  availableValuesAdded: {
    [contractCode: string]: ValueAddedTuple[];
  };
  valuesAddedWithoutDco: {
    [index: number]: {
      specialized: boolean;
      nothasNotAlloedCombinations: boolean;
    };
  };
}

export const initialState: ValuesAddedState = {
  availableValuesAdded: {},
  valuesAddedWithoutDco: {},
};

/**
 * Makes tuple state into loading state given index
 * Will remove further tuples from index
 * @param state
 * @param index
 */
const loadTupleState = (state: ValueAddedTuple[], index: number) => {
  if (index === 0) {
    state = [];
  } else {
    state.splice(index + 1, state.length - (index + 1));
  }

  state[index] = {
    valuesAdded: undefined,
    technicalProducts: [],
    loading: true,
  };

  return state;
};

/**
 * Makes a tuple state into a complete mode
 * Adds available added values options into given index
 * @param state
 * @param addedValues
 * @param index
 */
const completeTupleState = (
  state: ValueAddedTuple[],
  addedValues: ValueAddedOP[],
  technicalProduct: TechnicalProductOP[],
  index: number,
) => {
  return (
    !!state &&
    state.map((tuple, i) => {
      if (i === index) {
        return {
          valuesAdded: addedValues,
          technicalProducts: technicalProduct,
          loading: false,
        } as ValueAddedTuple;
      } else {
        return tuple;
      }
    })
  );
};

export const valuesAddedReducer = createReducer(
  initialState,
  on(OrdersCatalogsActions.LoadValuesAdded, (state, { formId, contractCode, valueAddedIndex }) => {
    const keyName = `${contractCode}_${formId}`;
    return {
      ...state,
      availableValuesAdded: {
        ...state.availableValuesAdded,
        [keyName]: loadTupleState(state.availableValuesAdded[keyName], valueAddedIndex),
      },
    } as ValuesAddedState;
  }),

  on(OrdersCatalogsActions.LoadValuesAddedMX, (state, { formId, contractCode, valueAddedIndex }) => {
    const keyName = `${contractCode}_${formId}`;
    return {
      ...state,
      availableValuesAdded: {
        ...state.availableValuesAdded,
        [keyName]: loadTupleState(state.availableValuesAdded[keyName], valueAddedIndex),
      },
    } as ValuesAddedState;
  }),

  on(
    OrdersCatalogsActions.LoadValuesAddedSuccess,
    (state, { formId, contractCode, valueAddedIndex, valuesAdded, technicalProducts }) => {
      const keyName = `${contractCode}_${formId}`;
      return {
        ...state,
        availableValuesAdded: {
          ...state.availableValuesAdded,
          [keyName]: completeTupleState(
            state.availableValuesAdded[keyName],
            valuesAdded,
            technicalProducts,
            valueAddedIndex,
          ),
        },
      } as ValuesAddedState;
    },
  ),

  on(OrdersCatalogsActions.DuplicateValueAddedTupleData, (state, { key, tuple }) => {
    return {
      ...state,
      availableValuesAdded: {
        ...state.availableValuesAdded,
        [key]: tuple,
      },
    } as ValuesAddedState;
  }),

  on(OrdersCatalogsActions.ResetValueAdded, (state) => ({
    ...initialState,
  })),
  on(OrdersCatalogsActions.RemoveValueAddedIndexMx, (state, { formId, contractCode, valueAddedIndex }) => {
    const keyName = `${contractCode}_${formId}`;
    const current = state.availableValuesAdded[keyName] || [];
    // Remove all tuples from valueAddedIndex onwards
    const updated = current.slice(0, valueAddedIndex);
    return {
      ...state,
      availableValuesAdded: {
        ...state.availableValuesAdded,
        [keyName]: updated,
      },
    } as ValuesAddedState;
  }),
);

export function reducer(state: ValuesAddedState | undefined, action: Action) {
  return valuesAddedReducer(state, action);
}
